import { formatIDR } from "@marketplace/shared/utils";
import {
  Button,
  Fieldset,
  Loader,
  Modal,
  NumberInput,
  Radio,
  TextInput
} from "@marketplace/ui";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle } from "react-icons/fa";
import { FaPlus } from "react-icons/fa";
import { FaMinus } from "react-icons/fa";

import { ProtectedPdfViewer } from "@/components";
import { FreeTrialPill } from "@/modules/shared";

type PlatformFeeType = "percentage" | "nominal";

export type PlatformFeeData = {
  packageId: number | undefined;
  packageName: string | undefined;
  isFreeTrial: boolean;
  targetMarginType: PlatformFeeType;
  percentageValue: number | string;
  nominalValue: number | string;
  variants: Variant[];
};

type Variant = {
  id: number;
  name: string;
  price: number;
  quantity: number;
  unit: string;
  commission_fee_percent: number;
  commission_fee_amount: number;
  price_with_comission: number;
  product_package_id: number;
  enabled?: boolean;
};

type PackageData = {
  id: number;
  name: string;
  product_id: string;
  specifications: string[];
  variants: Variant[];
  is_free_trial: boolean;
  created_at: string;
  updated_at: string;
  pks_file?: string;
};

type PlatformFeePackageModalProps = {
  opened: boolean;
  onClose: () => void;
  onNext: (data: PlatformFeeData) => void;
  onBack?: () => void;
  packageNumber?: number;
  totalPackages?: number;
  packageData?: any[] | PackageData;
  isEditMode?: boolean;
  productDetailLoading?: boolean;
  pksFile?: string;
  savedData?: PlatformFeeData;
};

export const PlatformFeeInputModal = ({
  opened,
  onClose,
  onNext,
  onBack,
  packageNumber = 1,
  totalPackages = 3,
  packageData,
  isEditMode = false,
  productDetailLoading = false,
  pksFile,
  savedData
}: PlatformFeePackageModalProps) => {
  const { t: tCommon } = useTranslation("common");
  const { t: tProduct } = useTranslation("product");

  const [targetMarginType, setTargetMarginType] =
    useState<PlatformFeeType>("percentage");
  const [percentageValue, setPercentageValue] = useState<number | string>("");
  const [nominalValue, setNominalValue] = useState<number | string>("");
  const [percentageError, setPercentageError] = useState<string | null>(null);

  const [variants, setVariants] = useState<Variant[]>([]);
  const [isDisplayFile, setIsDisplayFile] = useState<boolean>(false);

  const currentPackage = Array.isArray(packageData)
    ? packageData[packageNumber - 1]
    : packageData;
  const isFreeTrial = currentPackage?.is_free_trial ?? false;
  const pkg = currentPackage;
  const isPksFile = !!pksFile;
  const [isShowDoc, setIsShowDoc] = useState(isPksFile);

  const resetToDefault = () => {
    setNominalValue("");
    setPercentageValue("");
    setPercentageError(null);
    setTargetMarginType("percentage");
  };

  useEffect(() => {
    if (opened && pkg) {
      if (savedData) {
        setTargetMarginType(savedData.targetMarginType);
        setPercentageValue(savedData.percentageValue);
        setNominalValue(savedData.nominalValue);
        setPercentageError(null);
        if (savedData.variants && savedData.variants.length > 0) {
          setVariants(savedData.variants.map((v) => ({ ...v, enabled: true })));
        }
      } else if (isEditMode) {
        const feeType = pkg.platform_fee_type;
        const feePercentage = pkg.platform_fee_percentage;
        const feeNominal = pkg.platform_fee_nominal;

        if (feeType === "percentage" && feePercentage != null) {
          setTargetMarginType("percentage");
          setPercentageValue(feePercentage);
          setNominalValue("");
          if (feePercentage > 100) {
            setPercentageError(
              tProduct("modal.platformFeeSelectionModal.percentage.error")
            );
          }
        } else if (feeType === "nominal" && feeNominal != null) {
          setTargetMarginType("nominal");
          setNominalValue(feeNominal);
          setPercentageValue("");
        } else {
          setTargetMarginType("percentage");
          setPercentageValue("");
          setNominalValue("");
        }
      } else {
        resetToDefault();
      }

      if (!savedData && pkg?.variants && pkg.variants.length > 0) {
        const initializedVariants = pkg.variants.map((variant: any) => ({
          ...variant,
          enabled: true
        }));
        setVariants(initializedVariants);
      }
    }
  }, [opened, pkg, packageNumber, isEditMode, savedData]);

  useEffect(() => {
    if (opened && isPksFile) {
      setIsShowDoc(true);
    }
  }, [isPksFile, opened]);

  const calculatePublishedPrice = (basePrice: number): number => {
    if (targetMarginType === "percentage" && percentageValue) {
      const percentage =
        typeof percentageValue === "number"
          ? percentageValue
          : parseFloat(String(percentageValue));
      if (!isNaN(percentage)) {
        return basePrice * (1 + percentage / 100);
      }
    } else if (targetMarginType === "nominal" && nominalValue) {
      const nominal =
        typeof nominalValue === "number"
          ? nominalValue
          : parseFloat(String(nominalValue));
      if (!isNaN(nominal)) {
        return basePrice + nominal;
      }
    }
    return basePrice;
  };

  const formatNumber = (amount: number): string => {
    return new Intl.NumberFormat("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const updateVariantPrice = (id: number, value: number | string) => {
    const numericValue =
      typeof value === "number"
        ? value
        : parseFloat(String(value).replace(/\D/g, ""));
    if (!isNaN(numericValue)) {
      setVariants((prev) =>
        prev.map((v) => (v.id === id ? { ...v, price: numericValue } : v))
      );
    }
  };

  const handleNext = () => {
    const updatedVariants = variants.map((variant) => {
      const publishedPrice = calculatePublishedPrice(variant.price);
      const marginAmount = publishedPrice - variant.price;
      const marginPercent = (marginAmount / variant.price) * 100;

      return {
        ...variant,
        price_with_comission: publishedPrice,
        commission_fee_amount:
          targetMarginType === "nominal"
            ? marginAmount
            : variant.commission_fee_amount,
        commission_fee_percent:
          targetMarginType === "percentage"
            ? marginPercent
            : variant.commission_fee_percent
      };
    });

    onNext({
      packageId: pkg?.id,
      packageName: pkg?.name,
      isFreeTrial,
      targetMarginType,
      percentageValue,
      nominalValue,
      variants: updatedVariants
    });

    if (!isEditMode) {
      resetToDefault();
    }
  };

  const handleBack = () => {
    if (!isEditMode) {
      resetToDefault();
    }

    onBack?.();
  };

  const handleClose = () => {
    if (!isEditMode) {
      resetToDefault();
    }
    onClose();
  };

  if (productDetailLoading) {
    return (
      <div className="text-fjord-grey-500 text-center py-8">
        <Loader />
      </div>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={tProduct("modal.platformFeeSelectionModal.title")}
      zIndex={401}
      size="lg"
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3
            className={clsx(
              "text-lg font-semibold text-gray-700 ",
              isFreeTrial && "flex items-center"
            )}
          >
            {pkg?.name || `Package ${packageNumber}`}

            {isFreeTrial && (
              <span className="ml-2">
                <FreeTrialPill />
              </span>
            )}
          </h3>
          {isDisplayFile ? (
            <span className="text-sm text-fjord-grey-500">See Documents</span>
          ) : (
            <span className="text-sm text-fjord-grey-500">
              {tProduct("addProductPage.form.labels.package")}({packageNumber}/
              {totalPackages})
            </span>
          )}
        </div>
        {isShowDoc && (
          <div className="flex items-center gap-4 text-fjord-grey-500">
            <p className="text-sm">
              PKS Document - {pksFile?.split("/").pop()}
            </p>
            {isDisplayFile ? (
              <span
                className=" cursor-pointer"
                onClick={() => setIsDisplayFile(false)}
              >
                <FaMinus />
              </span>
            ) : (
              <span
                className="cursor-pointer"
                onClick={() => setIsDisplayFile(true)}
              >
                <FaPlus />
              </span>
            )}
          </div>
        )}
        {isDisplayFile && (
          <div className="mx-auto shrink-0 w-[600px] h-[700px] bg-fjord-grey-100 overflow-hidden ">
            <ProtectedPdfViewer
              src={`media/${pksFile}`}
              className="w-full h-full"
            />
          </div>
        )}

        {/* Target Margin Section */}
        {!isFreeTrial && !isDisplayFile && (
          <>
            <Fieldset>
              <div className="py-4 flex items-center justify-between">
                <h3 className="font-normal text-fjord-grey-500">
                  {tProduct("modal.platformFeeSelectionModal.targetMargin")}{" "}
                  <span className="text-red-500">*</span>
                </h3>
              </div>
              <Radio.Group
                value={targetMarginType}
                onChange={(value) => {
                  setTargetMarginType(value as PlatformFeeType);
                  if (value === "nominal" && !isEditMode) {
                    setPercentageValue("");
                  }
                  if (value === "percentage" && !isEditMode) {
                    setNominalValue("");
                  }
                }}
              >
                <div className="grid grid-cols-2 gap-x-4">
                  <NumberInput
                    label={
                      <Radio
                        value="percentage"
                        label={tProduct(
                          "modal.platformFeeSelectionModal.percentage.label"
                        )}
                      />
                    }
                    placeholder="0%"
                    value={percentageValue}
                    onChange={(value) => {
                      setPercentageValue(value);
                    }}
                    disabled={targetMarginType !== "percentage"}
                    rightSection={<span className="text-gray-400">%</span>}
                    hideControls
                    allowDecimal={true}
                    allowNegative={false}
                    error={percentageError}
                  />
                  <NumberInput
                    label={
                      <Radio
                        value="nominal"
                        label={tProduct(
                          "modal.platformFeeSelectionModal.nominal.label"
                        )}
                      />
                    }
                    placeholder="0"
                    value={nominalValue}
                    onChange={(value) => {
                      setNominalValue(value);
                    }}
                    disabled={targetMarginType !== "nominal"}
                    hideControls
                    allowDecimal={false}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowNegative={false}
                  />
                </div>
              </Radio.Group>
            </Fieldset>

            {/* Product Prices Section */}
            <div className="flex flex-col gap-y-4">
              {variants.length === 0 ? (
                <div className="text-center py-8 text-fjord-grey-500">
                  <p>
                    {tProduct(
                      "modal.platformFeeSelectionModal.noPackageVariants"
                    )}
                  </p>
                </div>
              ) : (
                variants.map((variant) => {
                  const publishedPrice = calculatePublishedPrice(variant.price);
                  const hasMargin =
                    targetMarginType === "percentage"
                      ? !!percentageValue
                      : !!nominalValue;
                  const marginPercentage = (
                    ((publishedPrice - variant.price) / variant.price) *
                    100
                  ).toFixed(1);
                  const marginNominal = publishedPrice - variant.price;

                  return (
                    <div key={variant.id} className="flex flex-col gap-y-2">
                      <div className="flex items-start gap-x-3">
                        <div className="flex-1">
                          <TextInput
                            label={
                              <div className="flex gap-2">
                                <FaCheckCircle fill="#0648FF" />
                                <span>
                                  {tProduct(
                                    "modal.platformFeeSelectionModal.price.title"
                                  )}
                                </span>
                              </div>
                            }
                            value={formatNumber(variant.price)}
                            onChange={(e) =>
                              updateVariantPrice(variant.id, e.target.value)
                            }
                            disabled
                            placeholder="0"
                            className="w-full"
                          />
                        </div>
                        <div className="flex-1 flex flex-col items-end justify-end h-full pt-6">
                          {hasMargin && (
                            <p className="text-xs text-fjord-grey-500 mb-1">
                              {tProduct(
                                "modal.platformFeeSelectionModal.price.description"
                              )}{" "}
                              <span className="text-red-500">
                                {targetMarginType === "percentage"
                                  ? `(${marginPercentage}%)`
                                  : `(${formatIDR(marginNominal)})`}
                              </span>
                            </p>
                          )}
                          <span className="text-base font-semibold text-blue-600">
                            {formatIDR(publishedPrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        <div>
          {!isDisplayFile && (
            <p className="text-sm text-fjord-grey-500">
              {tProduct("modal.platformFeeSelectionModal.viewEstimatedPrice")}
            </p>
          )}
          {/* Action Buttons */}
          <div className="flex justify-end items-center gap-x-[14px] pt-4 border-t">
            {packageNumber === 1 && onBack && isPksFile && !isShowDoc && (
              <Button
                variant="secondary"
                className="w-[128px]"
                onClick={() => {
                  setIsShowDoc(true);
                }}
              >
                {tCommon("back")}
              </Button>
            )}
            {packageNumber > 1 && onBack && (
              <Button
                variant="secondary"
                className="w-[128px]"
                onClick={handleBack}
              >
                {tCommon("back")}
              </Button>
            )}
            <Button
              variant="primary"
              className="w-[128px]"
              onClick={handleNext}
              disabled={
                !isFreeTrial &&
                (targetMarginType === "percentage"
                  ? !percentageValue || percentageValue === ""
                  : !nominalValue || nominalValue === "")
              }
            >
              {tCommon("next")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
